import { c } from "./colors";

const MAX_CELL_WIDTH = 40

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isObjectArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPlainObject)
}

const WHOP_TAG = /^[a-z]+_[A-Za-z0-9]+$/

/** Plain truncated string — used for column width calculation (no ANSI codes). */
function cellText(value: unknown): string {
  if (value == null) return ''
  const text = String(value)
  return text.length > MAX_CELL_WIDTH ? text.slice(0, MAX_CELL_WIDTH - 1) + '…' : text
}

/** Colored display string — used for rendering. */
function formatCell(value: unknown): string {
  const text = cellText(value)
  if (typeof value === 'boolean') return value ? c.success(text) : c.error(text)
  if (typeof value === 'string' && WHOP_TAG.test(value)) return c.id(text)
  return text
}

/** Total printable width of one row: sum of column widths plus two-space separators between them. */
function totalRowWidth(colWidths: number[]): number {
  const columnTotal = colWidths.reduce((sum, colWidth) => sum + colWidth, 0)
  return columnTotal + (colWidths.length - 1) * 2
}

/**
 * Renders an array of plain objects as an aligned terminal grid.
 * Drops columns from the right when the row would exceed the terminal width.
 */
function renderRows(rows: Record<string, unknown>[]): string {
  // Only include keys that are scalar across every row — a column where some rows
  // have an object and others have null would render as "[object Object]".
  const isScalar = (value: unknown) => typeof value !== 'object' || value === null
  const scalarKeys = Object.keys(rows[0]!).filter((key) =>
    rows.every((row) => isScalar(row[key])),
  )

  if (scalarKeys.length === 0) return '(no displayable columns)'

  // Column width = max of the header label and the widest cell value across all rows.
  const allWidths = scalarKeys.map((key) =>
    Math.max(key.length, ...rows.map((row) => cellText(row[key]).length)),
  )

  // Drop the rightmost columns until the row fits within the terminal width.
  const termWidth = process.stdout.columns ?? 80
  let numVisible = allWidths.length
  while (numVisible > 1 && totalRowWidth(allWidths.slice(0, numVisible)) > termWidth) {
    numVisible--
  }

  const visibleKeys = scalarKeys.slice(0, numVisible)
  const visibleWidths = allWidths.slice(0, numVisible)

  const header = visibleKeys
    .map((key, i) => c.tableHeader(key.toUpperCase().padEnd(visibleWidths[i]!)))
    .join('  ')

  const separator = c.separator(visibleWidths.map((w) => '─'.repeat(w)).join('──'))

  const body = rows.map((row) =>
    visibleKeys
      .map((key, i) => {
        const value = row[key]
        const formatted = formatCell(value)
        const width = visibleWidths[i]!
        const pad = width - cellText(value).length
        // Right-align numbers, left-align everything else.
        return typeof value === 'number'
          ? c.number(' '.repeat(pad) + formatted)
          : formatted + ' '.repeat(pad)
      })
      .join('  '),
  )

  const hiddenCount = scalarKeys.length - numVisible
  const overflowHint =
    hiddenCount > 0
      ? `\n${c.muted(`& ${hiddenCount} more columns — use --format json to see all`)}`
      : ''

  return [header, separator, ...body].join('\n') + overflowHint
}

/**
 * Recursively drops empty values (null, undefined, empty arrays, and objects
 * that become empty once pruned), counting how many fields were dropped so the
 * renderer can tell the user what's hidden.
 */
function pruneEmpty(value: unknown): { kept: unknown; hidden: number } {
  if (value == null) return { kept: undefined, hidden: 1 }

  if (Array.isArray(value)) {
    if (value.length === 0) return { kept: undefined, hidden: 1 }
    if (!value.every(isPlainObject)) return { kept: value, hidden: 0 }
    let hidden = 0
    const items: Record<string, unknown>[] = []
    for (const item of value) {
      const pruned = pruneEmpty(item)
      hidden += pruned.hidden
      if (pruned.kept !== undefined) items.push(pruned.kept as Record<string, unknown>)
    }
    if (items.length === 0) return { kept: undefined, hidden }
    return { kept: items, hidden }
  }

  if (isPlainObject(value)) {
    let hidden = 0
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      const pruned = pruneEmpty(entry)
      hidden += pruned.hidden
      if (pruned.kept !== undefined) out[key] = pruned.kept
    }
    if (Object.keys(out).length === 0) return { kept: undefined, hidden: Math.max(hidden, 1) }
    return { kept: out, hidden }
  }

  return { kept: value, hidden: 0 }
}

/** Colored display string for a scalar in the detail view — no truncation. */
function formatScalar(value: unknown): string {
  const text = String(value)
  if (typeof value === 'boolean') return value ? c.success(text) : c.error(text)
  if (typeof value === 'number') return c.number(text)
  if (typeof value === 'string') {
    if (WHOP_TAG.test(value)) return c.id(text)
    if (/^https?:\/\//.test(value)) return c.link(text)
  }
  return text
}

function renderEntries(obj: Record<string, unknown>, depth: number): string[] {
  const pad = '  '.repeat(depth)
  const lines: string[] = []
  for (const [key, value] of Object.entries(obj)) {
    const label = c.muted(`${key}:`)
    if (isPlainObject(value)) {
      lines.push(`${pad}${label}`)
      lines.push(...renderEntries(value, depth + 1))
    } else if (isObjectArray(value)) {
      lines.push(`${pad}${label}`)
      for (const item of value) {
        const itemLines = renderEntries(item, depth + 1)
        itemLines[0] = `${pad}- ${itemLines[0]!.slice(pad.length + 2)}`
        lines.push(...itemLines)
      }
    } else if (Array.isArray(value)) {
      lines.push(`${pad}${label} ${value.map(formatScalar).join(', ')}`)
    } else {
      lines.push(`${pad}${label} ${formatScalar(value)}`)
    }
  }
  return lines
}

/**
 * Renders a single object (e.g. a create/get response) as key: value lines,
 * hiding empty fields instead of drowning the useful ones in nulls.
 */
function renderObject(data: Record<string, unknown>): string {
  const { kept, hidden } = pruneEmpty(data)
  const lines =
    kept === undefined
      ? [c.muted('(all fields empty)')]
      : renderEntries(kept as Record<string, unknown>, 0)
  const hint =
    hidden > 0
      ? `\n${c.muted(`${hidden} empty ${hidden === 1 ? 'field' : 'fields'} hidden — use --format json to see all`)}`
      : ''
  return lines.join('\n') + hint
}

/**
 * Human-mode renderer passed to incur as `renderer`.
 * Arrays and single-array wrapped responses (e.g. `{ accounts: [...], pagination: {...} }`)
 * become an aligned grid; any other object (create/get responses) becomes a
 * detail view with empty fields hidden. Returns null for non-object data,
 * falling back to incur's TOON formatter.
 */
export function render(data: unknown): string | null {
  if (Array.isArray(data)) {
    if (data.length === 0) return c.muted('(no results)')
    return isObjectArray(data) ? renderRows(data) : null
  }

  if (isPlainObject(data)) {
    const values = Object.values(data)
    // A wrapped list response has exactly one object-array field and no scalar
    // fields (only companions like `pagination`). A detail object with a nested
    // array (e.g. an account with `balances`) has scalars and gets the detail view.
    const hasScalarField = values.some((v) => v !== null && typeof v !== 'object')
    if (!hasScalarField) {
      const objectArrays = values.filter(isObjectArray)
      if (objectArrays.length === 1) return renderRows(objectArrays[0])
      const arrays = values.filter(Array.isArray)
      if (objectArrays.length === 0 && arrays.length === 1 && arrays[0]!.length === 0) {
        return c.muted('(no results)')
      }
    }
    return renderObject(data)
  }

  return null
}
